
import SidePanel from '~/components/SidePanel.jsx';
import { useFormDetailsQuery, useFormsQuery } from '~/lib/Queries';
import Select from '~/components/Select';
import { useMemo, useState } from 'react';
import useBusinessPostApi from '~/lib/useBusinessPostApi.js';
import * as Toast from '~/lib/Toast.js';
import EditFormQuestions from '~/components/scheduling/EditFormQuestions.jsx';
import ClipboardText from '~/components/icons/ClipboardText.jsx';

const OMIT_ADMIN_FORM_TYPES = [
    'scheduling',
    'classscheduling',
    'packages',
    'giftcertificate',
    'signature',
    'clientaccount',
    'agreetoterms',
    'hidden',
    'payment',
    'thankyou',
    'display',
    'videoembed',
    'invoiceheader',
    'invoicelineitems',
    'invoicesummary',
    'billinginfo',
];

const SectionHeading = ({ icon, name, className, children, action }) => {
    return (
        <div className={`bg-gray-150 rounded-lg p-4 flex items-center ${className}`}>
            {!!icon && (
                <div className="w-6 h-6 text-primary-700 mr-3">{icon}</div>
            )}
            <div className="text-lg font-medium flex-1">
                {name}
                {!!children && (
                    <div className="font-normal text-base">{children}</div>
                )}
            </div>
            {action}
        </div>
    );
};

const AddFormToClientPanel = ({ isOpen, onClose, clientId, onFormAdded }) => {
    const { data: allFormsData } = useFormsQuery();
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [answers, setAnswers] = useState({});

    const nonBookingForms = useMemo(() => {
        return (allFormsData || []).filter(form => !form.is_booking);
    }, [allFormsData]);

    const formOptions = useMemo(() => {
        return [
            { label: 'Select a form...', value: '' },
            ...nonBookingForms.map(form => ({
                label: form.name,
                value: form.id,
            })),
        ];
    }, [nonBookingForms]);

    const { data: formDetails, isLoading: isLoadingForm } =
        useFormDetailsQuery(selectedFormId || 0);

    const questions = useMemo(() => {
        if (!formDetails?.formSections) return [];
        const allQuestions = [];
        formDetails.formSections.forEach(({ questions }) => {
            (questions || []).forEach(q => {
                if (!OMIT_ADMIN_FORM_TYPES.includes(q.type)) {
                    allQuestions.push(q);
                }
            });
        });
        return allQuestions;
    }, [formDetails]);

    const { save, isSaving } = useBusinessPostApi(
        `/submissions/create-for-client/${clientId}`,
    );

    function handleClose() {
        setSelectedFormId(null);
        setAnswers({});
        onClose();
    }

    function doSave() {
        if (!selectedFormId) {
            Toast.error('Please select a form');
            return;
        }

        save({
            formId: selectedFormId,
            answers,
        }).then(() => {
            Toast.success('Form submitted successfully');
            handleClose();
            if (onFormAdded) onFormAdded();
        });
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={handleClose}
            title="Add Form to Client"
            showSave={true}
            showBackArrow={true}
            onSave={doSave}
            onCancel={handleClose}
            saveLabel={isSaving ? 'Saving...' : 'Save'}
        >
            <Select
                label="Form"
                options={formOptions}
                value={selectedFormId}
                onChange={value => {
                    setSelectedFormId(value);
                    setAnswers({});
                }}
                inputClassName="w-full"
            />

            {!!selectedFormId && (
                <div className="mt-4">
                    <EditFormQuestions
                        questions={questions}
                        isLoading={isLoadingForm}
                        answers={answers}
                        setAnswers={setAnswers}
                    />
                </div>
            )}
        </SidePanel>
    );
};

export default AddFormToClientPanel;

